import { ImageOff, Receipt } from "lucide-react";

import { medidaDoItem, qtdDoItem } from "../../lib/unidadeVenda";

import { reais } from "./etapas";

export const imagem = (url) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return url.startsWith("/uploads/") ? `/api${url}` : url;
};

// Produto por quilo/litro conta 1 item (1,250 kg não é "1,25 itens").
const contarItens = (itens, produtos) => (itens || []).reduce(
  (s, it) => s + (medidaDoItem(it, produtos) ? 1 : Number(it.quantidade || it.quantity || 1)), 0);

function Item({ item, produtos, todosProdutos, t }) {
  const foto = imagem(item.photo_url);
  // item.preco já inclui os subitens: a linha mostra só o preço base (os subitens vêm abaixo com o deles)
  const somaSub = (item.subitems || []).reduce((s, si) => s + (Number(si.preco) || 0), 0);
  const linha = Math.max(0, (Number(item.preco) || 0) - somaSub) * (Number(item.quantidade) || 1);
  const fotoSub = (nome) => {
    const n = String(nome || "").toLowerCase().trim();
    const p = (todosProdutos || []).find((x) => (x.name || x.nome || "").toLowerCase().trim() === n);
    return p ? imagem(p.photo_url || p.imagem || p.image) : null;
  };
  const etapas = [];
  const indice = {};
  (item.subitems || []).forEach((si) => {
    const k = si.step_name || "Adicionais";
    if (indice[k] === undefined) { indice[k] = etapas.length; etapas.push({ nome: k, itens: [] }); }
    etapas[indice[k]].itens.push(si);
  });
  return (
    <div className="flex gap-3 py-3">
      {foto ? (
        <img src={foto} alt="" className={`w-14 h-14 rounded-xl object-cover shrink-0 ${t.chip}`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <div className={`w-14 h-14 rounded-xl grid place-items-center shrink-0 ${t.chip} ${t.mudo}`}><ImageOff className="w-5 h-5" /></div>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-semibold leading-tight ${t.texto}`}>
            <span className="text-orange-500 font-bold mr-1">{qtdDoItem(Number(item.quantidade) || 1, medidaDoItem(item, produtos))}</span>
            {item.nome}
          </p>
          <p className={`text-sm font-bold whitespace-nowrap ${t.texto}`}>{reais(linha)}</p>
        </div>
        {etapas.map((g) => (
          <div key={g.nome} className="mt-1.5">
            <p className={`text-[10px] uppercase tracking-wider font-bold ${t.mudo}`}>{g.nome}</p>
            {g.itens.map((si, k) => {
              const valor = Number(si.preco) || 0; // já é o total da linha
              const fs = imagem(si.photo_url) || fotoSub(si.nome);
              return (
                <div key={k} className="flex items-center justify-between gap-2 text-xs mt-1">
                  <span className={`flex items-center gap-1.5 min-w-0 ${t.mudo}`}>
                    {fs && <img src={fs} alt="" className="w-6 h-6 rounded object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                    <span className="truncate">{(Number(si.quantidade) || 1) > 1 ? `${si.quantidade}x ` : ""}{si.nome}</span>
                  </span>
                  {valor > 0 ? <span className={`shrink-0 ${t.mudo}`}>+{reais(valor)}</span> : <span className="shrink-0 text-emerald-500 font-bold">GRÁTIS</span>}
                </div>
              );
            })}
          </div>
        ))}
        {!etapas.length && (item.etapas || []).map((ep, k) => {
          const itens = ep.itens || ep.items || [];
          if (!itens.length) return null;
          return (
            <div key={k} className="mt-1.5">
              <p className={`text-[10px] uppercase tracking-wider font-bold ${t.mudo}`}>{ep.etapa || ep.stepName || ep.name || "Adicionais"}</p>
              {itens.map((nome, j) => <p key={j} className={`text-xs ${t.mudo}`}>+ {nome}</p>)}
            </div>
          );
        })}
        {(item.observacao || "").trim() && (
          <p className="mt-1.5 px-2 py-1 rounded-md text-xs bg-amber-500/10 text-amber-600 dark:text-amber-300 border border-amber-500/30">
            <b>Obs:</b> {item.observacao}
          </p>
        )}
      </div>
    </div>
  );
}

function Linha({ rotulo, valor, verde, t }) {
  return (
    <div className="flex justify-between text-sm">
      <span className={t.mudo}>{rotulo}</span>
      <span className={verde ? "text-emerald-500 font-semibold" : t.texto}>{verde ? `− ${reais(valor)}` : reais(valor)}</span>
    </div>
  );
}

/**
 * Os itens do pedido, já abertos: adicionais, observações e o resumo de valores
 * (dono, 24/10/2026: antes vinham recolhidos numa linha e o cliente não via).
 */
export default function ItensDoPedido({ pedido, retirada, produtos, todosProdutos, t }) {
  const itens = pedido?.items || [];
  if (!itens.length) return null;
  const qtd = contarItens(itens, produtos);
  return (
    <section className={`rounded-3xl border overflow-hidden ${t.card} ${t.borda}`} data-e2e="acompanhamento-itens">
      <div className="flex items-center gap-3 p-4">
        <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${t.chip} ${t.texto}`}><Receipt className="w-5 h-5" /></span>
        <span className="min-w-0 flex-1">
          <span className={`block text-sm font-bold ${t.texto}`}>Seu pedido</span>
          <span className={`block text-xs ${t.mudo}`}>{qtd} {qtd === 1 ? "item" : "itens"} · {reais(pedido?.total)}</span>
        </span>
      </div>
      <div className={`px-4 border-t divide-y ${t.borda} ${t.divisoria}`}>
        {itens.map((it, k) => <Item key={k} item={it} produtos={produtos} todosProdutos={todosProdutos} t={t} />)}
      </div>
      <div className={`px-4 py-3 border-t space-y-1.5 ${t.borda} ${t.fundoResumo}`}>
        {Number(pedido?.valor_entrega) > 0 && !retirada && <Linha rotulo="Taxa de entrega" valor={pedido.valor_entrega} t={t} />}
        {pedido?.cupom_codigo && Number(pedido?.cupom_desconto) > 0 && (
          <Linha rotulo={String(pedido.cupom_codigo).startsWith("PREMIO-") ? "Prêmio do cartão fidelidade" : `Cupom (${pedido.cupom_codigo})`}
            valor={pedido.cupom_desconto} verde t={t} />
        )}
        {Number(pedido?.desconto_manual) > 0 && <Linha rotulo="Desconto" valor={pedido.desconto_manual} verde t={t} />}
        {Number(pedido?.cashback_usado) > 0 && <Linha rotulo="Cashback usado" valor={pedido.cashback_usado} verde t={t} />}
        <div className={`flex justify-between text-base font-bold pt-1.5 border-t ${t.borda}`}>
          <span className={t.texto}>Total</span>
          <span className="text-orange-500">{reais(pedido?.total)}</span>
        </div>
      </div>
    </section>
  );
}
